import React, { useState, useEffect, useContext } from "react";
import ZenPagination from "../components/ZenPagination";
import {
  Archive, RotateCcw, Trash2, ShoppingCart, FileText, Loader2,
  AlertTriangle, CheckCircle2, RefreshCw, Package, Search, Filter,
} from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { AuthCtx } from "../appContexts";
import { STATUS_LABELS } from "../mock";

const STATUS_DOT_COLOR = {
  sky: "bg-sky-500", slate: "bg-slate-500", indigo: "bg-indigo-500", amber: "bg-amber-500",
  violet: "bg-violet-500", orange: "bg-orange-500", emerald: "bg-emerald-500",
  green: "bg-green-500", rose: "bg-rose-500", teal: "bg-teal-500",
};

export default function ArchivePage() {
  const { user } = useContext(AuthCtx);
  const isAdmin = user?.role === "admin";

  const [orders, setOrders] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("orders"); // orders | quotes
  const [q, setQ] = useState("");
  const [restoring, setRestoring] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [clearingAll, setClearingAll] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/archive");
      setOrders(data.orders || []);
      setQuotes(data.quotes || []);
    } catch (e) {
      toast.error("Arşiv yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const restoreOrder = async (id) => {
    setRestoring(id);
    try {
      await api.put(`/archive/orders/${id}/restore`);
      toast.success("Sipariş geri yüklendi");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Geri yüklenemedi");
    } finally {
      setRestoring(null);
    }
  };

  const restoreQuote = async (id) => {
    setRestoring(id);
    try {
      await api.put(`/archive/quotes/${id}/restore`);
      toast.success("Teklif geri yüklendi");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Geri yüklenemedi");
    } finally {
      setRestoring(null);
    }
  };

  const permDeleteOrder = async (id, label) => {
    if (!window.confirm(`"${label}" kalıcı olarak silinsin mi? Bu işlem GERİ ALINAMAZ.`)) return;
    setDeleting(id);
    try {
      await api.delete(`/archive/orders/${id}`);
      toast.success("Kalıcı olarak silindi");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Silinemedi");
    } finally {
      setDeleting(null);
    }
  };

  const permDeleteQuote = async (id, label) => {
    if (!window.confirm(`"${label}" kalıcı olarak silinsin mi? Bu işlem GERİ ALINAMAZ.`)) return;
    setDeleting(id);
    try {
      await api.delete(`/archive/quotes/${id}`);
      toast.success("Kalıcı olarak silindi");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Silinemedi");
    } finally {
      setDeleting(null);
    }
  };

  const clearAll = async () => {
    const total = orders.length + quotes.length;
    if (!window.confirm(`Arşivdeki TÜM ${total} kayıt kalıcı silinsin mi? GERİ ALINAMAZ!`)) return;
    setClearingAll(true);
    try {
      const { data } = await api.delete("/archive/clear-all");
      toast.success(`${data.deleted} kayıt kalıcı olarak silindi`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Temizlenemedi");
    } finally {
      setClearingAll(false);
    }
  };

  const filteredOrders = orders.filter(o =>
    !q || (o.customerName + " " + (o.orderNo || "") + " " + o.id).toLowerCase().includes(q.toLowerCase())
  );
  const filteredQuotes = quotes.filter(q2 =>
    !q || (q2.customerName + " #" + (q2.quoteNo || "") + " " + q2.id).toLowerCase().includes(q.toLowerCase())
  );
  const pagedOrders = filteredOrders.slice((page - 1) * pageSize, page * pageSize);
  const pagedQuotes = filteredQuotes.slice((page - 1) * pageSize, page * pageSize);
  useEffect(() => { setPage(1); }, [q, activeTab]);

  const total = orders.length + quotes.length;

  return (
    <div className="space-y-4 zen-anim-up">
      {/* Header */}
      <div className="flex flex-col md:flex-row gap-3 md:items-center justify-between">
        <h2 className="text-2xl font-extrabold tracking-tight flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-slate-600 to-slate-800 flex items-center justify-center shadow-md">
            <Archive className="w-5 h-5 text-white" />
          </span>
          Arşiv
          {total > 0 && (
            <span className="ml-1 text-xs font-bold px-2 py-0.5 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
              {total}
            </span>
          )}
        </h2>
        <div className="flex gap-2 flex-wrap items-center">
          <div className="flex items-center gap-2 zen-panel px-3">
            <Search className="w-4 h-4 text-slate-400" />
            <input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Ara..."
              className="bg-transparent outline-none text-sm py-2 w-44"
              data-testid="archive-search"
            />
          </div>
          <button
            onClick={load}
            className="zen-btn-ghost !p-2.5"
            title="Yenile"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
          {isAdmin && total > 0 && (
            <button
              data-testid="archive-clear-all-btn"
              onClick={clearAll}
              disabled={clearingAll}
              className="zen-btn-ghost border border-red-200 dark:border-red-900/40 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/20 !text-xs"
            >
              {clearingAll ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              Tümünü Kalıcı Sil
            </button>
          )}
        </div>
      </div>

      {/* Info banner */}
      <div className="zen-panel p-3 border-l-4 border-amber-400 bg-amber-50/50 dark:bg-amber-950/20 flex items-start gap-2.5">
        <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
        <p className="text-xs text-amber-700 dark:text-amber-400">
          Arşivdeki kayıtlar sisteminizde görünmez. <strong>Geri Yükle</strong> ile aktif listeye alabilirsiniz.{" "}
          {isAdmin ? <><strong>Kalıcı Sil</strong> yalnızca admin tarafından yapılabilir ve geri alınamaz.</> : "Kalıcı silme işlemi yalnızca yetkili yönetici tarafından yapılabilir."}
        </p>
      </div>

      {/* Tabs */}
      <div className="zen-panel overflow-hidden">
        <div className="flex border-b border-slate-200 dark:border-[#22304A]">
          <button
            onClick={() => setActiveTab("orders")}
            data-testid="archive-tab-orders"
            className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold transition-colors
              ${activeTab === "orders"
                ? "border-b-2 border-[#E5322A] text-[#E5322A]"
                : "text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"}`}
          >
            <ShoppingCart className="w-4 h-4" />
            Siparişler
            <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 font-bold">
              {orders.length}
            </span>
          </button>
          <button
            onClick={() => setActiveTab("quotes")}
            data-testid="archive-tab-quotes"
            className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold transition-colors
              ${activeTab === "quotes"
                ? "border-b-2 border-[#E5322A] text-[#E5322A]"
                : "text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"}`}
          >
            <FileText className="w-4 h-4" />
            Teklifler
            <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 font-bold">
              {quotes.length}
            </span>
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400 gap-2">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="text-sm">Yükleniyor...</span>
          </div>
        ) : (
          <>
            {/* Orders Tab */}
            {activeTab === "orders" && (
              filteredOrders.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-slate-400 gap-3">
                  <CheckCircle2 className="w-10 h-10 text-emerald-400" />
                  <p className="text-sm font-medium">Arşivde sipariş yok</p>
                </div>
              ) : (
                <table className="zen-table zen-table--compact">
                  <thead>
                    <tr>
                      <th>Sipariş No</th>
                      <th>Cari</th>
                      <th>Durum</th>
                      <th>Tutar</th>
                      <th>Arşivlenme</th>
                      <th>Arşivleyen</th>
                      <th className="text-center">İşlem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedOrders.map(o => {
                      const stat = STATUS_LABELS[o.status] || { tr: o.status, color: "slate" };
                      const archivedDate = o.archivedAt
                        ? new Date(o.archivedAt).toLocaleDateString("tr-TR")
                        : "-";
                      return (
                        <tr key={o.id} data-testid={`archive-order-${o.id}`} className="opacity-70 hover:opacity-100">
                          <td>
                            <span className="zen-mono text-xs font-bold text-[#E5322A]">
                              {o.orderNo ? `SP-${String(o.orderNo).padStart(4,"0")}` : o.id}
                            </span>
                          </td>
                          <td className="text-sm">{o.customerName || "-"}</td>
                          <td>
                            <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full
                              bg-${stat.color}-100 dark:bg-${stat.color}-900/30 text-${stat.color}-700 dark:text-${stat.color}-400`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT_COLOR[stat.color] || "bg-slate-400"}`} />
                              {stat.tr}
                            </span>
                          </td>
                          <td className="text-right text-sm font-semibold">
                            {(o.total || 0).toLocaleString("tr-TR", { maximumFractionDigits: 2 })} {o.currency || "TL"}
                          </td>
                          <td className="text-xs text-slate-500">{archivedDate}</td>
                          <td className="text-xs text-slate-500">{o.archivedBy || "-"}</td>
                          <td>
                            <div className="flex gap-1 justify-center">
                              <button
                                data-testid={`restore-order-${o.id}`}
                                onClick={() => restoreOrder(o.id)}
                                disabled={restoring === o.id}
                                className="zen-btn-ghost !p-1.5 text-emerald-600 hover:text-emerald-700"
                                title="Geri Yükle"
                              >
                                {restoring === o.id
                                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  : <RotateCcw className="w-3.5 h-3.5" />}
                              </button>
                              {isAdmin && (
                                <button
                                  data-testid={`perm-delete-order-${o.id}`}
                                  onClick={() => permDeleteOrder(o.id, o.customerName || o.id)}
                                  disabled={deleting === o.id}
                                  className="zen-btn-ghost !p-1.5 text-red-500 hover:text-red-700"
                                  title="Kalıcı Sil"
                                >
                                  {deleting === o.id
                                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    : <Trash2 className="w-3.5 h-3.5" />}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )
            )}
            {activeTab === "orders" && (
              <ZenPagination total={filteredOrders.length} page={page} pageSize={pageSize} onPage={setPage} onPageSize={(s) => { setPageSize(s); setPage(1); }} />
            )}

            {/* Quotes Tab */}
            {activeTab === "quotes" && (
              filteredQuotes.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-slate-400 gap-3">
                  <CheckCircle2 className="w-10 h-10 text-emerald-400" />
                  <p className="text-sm font-medium">Arşivde teklif yok</p>
                </div>
              ) : (
                <table className="zen-table zen-table--compact">
                  <thead>
                    <tr>
                      <th>Teklif No</th>
                      <th>Cari</th>
                      <th>Durum</th>
                      <th>Tutar</th>
                      <th>Arşivlenme</th>
                      <th>Arşivleyen</th>
                      <th className="text-center">İşlem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedQuotes.map(q2 => {
                      const archivedDate = q2.archivedAt
                        ? new Date(q2.archivedAt).toLocaleDateString("tr-TR")
                        : "-";
                      return (
                        <tr key={q2.id} data-testid={`archive-quote-${q2.id}`} className="opacity-70 hover:opacity-100">
                          <td>
                            <span className="zen-mono text-xs font-bold text-[#6D5CE6]">
                              #{q2.quoteNo || q2.id}
                            </span>
                          </td>
                          <td className="text-sm">{q2.customerName || "-"}</td>
                          <td className="text-xs text-slate-500">{q2.status || "-"}</td>
                          <td className="text-right text-sm font-semibold">
                            {(q2.total || 0).toLocaleString("tr-TR", { maximumFractionDigits: 2 })} {q2.currency || "TL"}
                          </td>
                          <td className="text-xs text-slate-500">{archivedDate}</td>
                          <td className="text-xs text-slate-500">{q2.archivedBy || "-"}</td>
                          <td>
                            <div className="flex gap-1 justify-center">
                              <button
                                data-testid={`restore-quote-${q2.id}`}
                                onClick={() => restoreQuote(q2.id)}
                                disabled={restoring === q2.id}
                                className="zen-btn-ghost !p-1.5 text-emerald-600 hover:text-emerald-700"
                                title="Geri Yükle"
                              >
                                {restoring === q2.id
                                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  : <RotateCcw className="w-3.5 h-3.5" />}
                              </button>
                              {isAdmin && (
                                <button
                                  data-testid={`perm-delete-quote-${q2.id}`}
                                  onClick={() => permDeleteQuote(q2.id, `Teklif #${q2.quoteNo || q2.id}`)}
                                  disabled={deleting === q2.id}
                                  className="zen-btn-ghost !p-1.5 text-red-500 hover:text-red-700"
                                  title="Kalıcı Sil"
                                >
                                  {deleting === q2.id
                                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    : <Trash2 className="w-3.5 h-3.5" />}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )
            )}
            {activeTab === "quotes" && (
              <ZenPagination total={filteredQuotes.length} page={page} pageSize={pageSize} onPage={setPage} onPageSize={(s) => { setPageSize(s); setPage(1); }} />
            )}
          </>
        )}
      </div>
    </div>
  );
}
